import React, { useState, useEffect } from 'react';
import type { AdminCustomer } from '../../services/adminApiService';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import './AdminComponents.css';
import './CustomerManagement.css';
import { useAppSelector } from '../../store/hooks';

const CustomerManagement: React.FC = () => {
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [groupedCustomers, setGroupedCustomers] = useState<CustomerGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedChildSections, setExpandedChildSections] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('id');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [detailsCustomerId, setDetailsCustomerId] = useState<number | null>(null);
  const [detailsCustomer, setDetailsCustomer] = useState<AdminCustomer | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<any>(null);

  // Types for grouping
  interface CustomerGroup {
    customerName: string;
    customerEmail: string;
    customers: AdminCustomer[];
    childCustomers: AdminCustomer[];
    totalBalance: number;
    totalPortfolioValue: number;
  }

  const { getRealCustomersWithAccounts, reactivateCustomer, createCurrencyAccount, getCustomerById } = useAdminApiService();
  const adminToken = useAppSelector(state => state.auth.token) ?? undefined;
  const adminUser = useAppSelector(state => state.auth.adminUser);

  const loadCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getRealCustomersWithAccounts(0, 100, 'id', 'DESC');
      if (response.success && response.data) {
        const customersData = response.data.content || [];
        setCustomers(customersData);
        setTotalPages(response.data.totalPages || 1);
        
        // Group parent and child customers together
        groupCustomers(customersData);
      } else {
        setError(response.error || 'Failed to load customers');
      }
    } catch (err) {
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []); // Empty dependency array since loadCustomers is stable

  // Fetch customer details when detailsCustomerId changes
  useEffect(() => {
    if (detailsCustomerId !== null) {
      setDetailsLoading(true);
      setDetailsError(null);
      setDetailsCustomer(null);
      getCustomerById(detailsCustomerId)
        .then(resp => {
          if (resp.success && resp.data) {
            setDetailsCustomer(resp.data);
          } else {
            setDetailsError(resp.error || 'Failed to load customer details');
          }
        })
        .catch(() => setDetailsError('Failed to load customer details'))
        .finally(() => setDetailsLoading(false));
    }
  }, [detailsCustomerId]); // Removed getCustomerById from dependencies to prevent duplicate calls

  // When detailsCustomer changes, update editForm
  useEffect(() => {
    if (detailsCustomer) {
      setEditForm({
        userType: detailsCustomer.userType || '',
        riskCategory: detailsCustomer.riskCategory || '',
        reviewRequired: detailsCustomer.reviewRequired || false,
        sepaDailyLimit: detailsCustomer.sepaDailyLimit ?? '',
        sepaMonthlyLimit: detailsCustomer.sepaMonthlyLimit ?? '',
        sepaYearlyLimit: detailsCustomer.sepaYearlyLimit ?? '',
        sepaSingleTransactionLimit: detailsCustomer.sepaSingleTransactionLimit ?? '',
        sepaLimitCurrency: detailsCustomer.sepaLimitCurrency || 'EUR',
      });
    }
  }, [detailsCustomer]);

  const groupCustomers = (customers: AdminCustomer[]) => {
    const customerMap = new Map<number, AdminCustomer>();
    const groups = new Map<string, CustomerGroup>();

    // Map all customers by id for easy lookup
    customers.forEach(customer => {
      customerMap.set(customer.id, customer);
    });

    // First, create groups for parent customers
    customers.forEach(customer => {
      if (!customer.parentCustomerId) {
        // This is a parent customer
        const customerName = `${customer.firstName} ${customer.lastName}`;
        if (!groups.has(customerName)) {
          groups.set(customerName, {
            customerName: customerName,
            customerEmail: customer.email,
            customers: [],
            childCustomers: [],
            totalBalance: 0,
            totalPortfolioValue: 0,
          });
        }
        const group = groups.get(customerName)!;
        group.customers.push(customer);
        
        // Calculate total balance from accounts
        const customerBalance = customer.accounts?.reduce((sum, account) => 
          sum + parseFloat(account.balance || '0'), 0) || 0;
        group.totalBalance += customerBalance;
        group.totalPortfolioValue += customer.portfolioSummary?.totalPortfolioValue || 0;
      }
    });

    // Now, add child customers to the correct parent group
    customers.forEach(customer => {
      if (customer.parentCustomerId) {
        // Find the parent customer
        const parentCustomer = customerMap.get(customer.parentCustomerId);
        if (parentCustomer) {
          // Find the group for the parent customer's name
          const parentName = `${parentCustomer.firstName} ${parentCustomer.lastName}`;
          const group = groups.get(parentName);
          if (group) {
            group.childCustomers.push(customer);
            
            // Calculate total balance from accounts
            const customerBalance = customer.accounts?.reduce((sum, account) => 
              sum + parseFloat(account.balance || '0'), 0) || 0;
            group.totalBalance += customerBalance;
            group.totalPortfolioValue += customer.portfolioSummary?.totalPortfolioValue || 0;
          }
        }
      }
    });

    setGroupedCustomers(Array.from(groups.values()));
  };

  const handleSuspendCustomer = (customerId: number) => {
    // Implementation needed
    console.log('Suspend customer:', customerId);
  };

  const handleReactivateCustomer = async (customerId: number) => {
    const reason = prompt('Enter reason for reactivation:');
    if (!reason) return;

    try {
      const response = await reactivateCustomer(customerId, reason);
      if (response.success) {
        await loadCustomers();
      } else {
        alert(response.error || 'Failed to reactivate customer');
      }
    } catch (err) {
      alert('Failed to reactivate customer');
    }
  };

  const handleRefresh = () => {
    loadCustomers();
  };

  const handleAddCurrencyAccount = async (customerId: number, currency: string) => {
    const accountType = 'CHECKING'; // or allow selection
    try {
      const response = await createCurrencyAccount(customerId, accountType, currency);
      if (response.success) {
        alert(`${currency} account created successfully!`);
        await loadCustomers();
      } else {
        alert(response.error || `Failed to create ${currency} account`);
      }
    } catch (err) {
      alert(`Failed to create ${currency} account`);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return 'N/A';
    }
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortDirection('ASC');
    }
  };

  const toggleGroup = (customerName: string) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (expandedGroups.has(customerName)) {
      newExpandedGroups.delete(customerName);
      // When collapsing a group, also collapse its child section
      setExpandedChildSections(prev => {
        const newSet = new Set(prev);
        newSet.delete(customerName);
        return newSet;
      });
    } else {
      newExpandedGroups.add(customerName);
      // When expanding a group, also expand its child section if there are child customers
      const group = groupedCustomers.find(g => g.customerName === customerName);
      if (group && group.childCustomers.length > 0) {
        setExpandedChildSections(prev => {
          const newSet = new Set(prev);
          newSet.add(customerName);
          return newSet;
        });
      }
    }
    setExpandedGroups(newExpandedGroups);
  };

  const toggleChildSection = (customerName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent group toggle
    const newExpandedChildSections = new Set(expandedChildSections);
    if (expandedChildSections.has(customerName)) {
      newExpandedChildSections.delete(customerName);
    } else {
      newExpandedChildSections.add(customerName);
    }
    setExpandedChildSections(newExpandedChildSections);
  };

  // Permission check: Only allow edit for compliance/admin
  const canEdit = adminUser && (
    adminUser.role === 'COMPLIANCE_OFFICER' ||
    (adminUser.permissions && adminUser.permissions.includes('MANAGE_COMPLIANCE_ALERTS'))
  );

  // Save handler
  const handleSave = async () => {
    if (!detailsCustomer) return;
    try {
      const updateData = {
        userType: editForm.userType,
        riskCategory: editForm.riskCategory,
        reviewRequired: editForm.reviewRequired,
        sepaDailyLimit: editForm.sepaDailyLimit === '' ? null : Number(editForm.sepaDailyLimit),
        sepaMonthlyLimit: editForm.sepaMonthlyLimit === '' ? null : Number(editForm.sepaMonthlyLimit),
        sepaYearlyLimit: editForm.sepaYearlyLimit === '' ? null : Number(editForm.sepaYearlyLimit),
        sepaSingleTransactionLimit: editForm.sepaSingleTransactionLimit === '' ? null : Number(editForm.sepaSingleTransactionLimit),
        sepaLimitCurrency: editForm.sepaLimitCurrency,
      };
      // Use adminApiService to update customer (assume updateCustomer method exists)
      const resp = await (window as any).adminApiService.updateCustomer(detailsCustomer.id, updateData);
      if (resp.success) {
        alert('Customer SEPA & Risk Controls updated successfully!');
        setEditMode(false);
        setDetailsCustomerId(null);
        setTimeout(() => loadCustomers(), 500);
      } else {
        alert(resp.error || 'Failed to update customer');
      }
    } catch (err) {
      alert('Failed to update customer');
    }
  };

  if (loading && customers.length === 0) {
    return (
      <div className="admin-component-loading">
        <div className="loading-spinner"></div>
        <p>Loading customers...</p>
      </div>
    );
  }

  if (error && customers.length === 0) {
    return (
      <div className="admin-component-error">
        <h3>⚠️ Error Loading Customers</h3>
        <p>{error}</p>
        <button onClick={loadCustomers} className="retry-btn">
          🔄 Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="customer-management">
      <div className="section-header">
        <div className="header-content">
          <h2>👥 Customer Management</h2>
          <p>Manage customer accounts and monitor user activity</p>
        </div>
        <div className="header-actions">
          <button onClick={handleRefresh} className="refresh-btn" disabled={loading}>
            {loading ? '🔄' : '🔄'} Refresh
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="filters-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search customers by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-controls">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="PENDING">Pending</option>
          </select>
        </div>
      </div>

      {/* Customers Table */}
      <div className="admin-table-container" style={{ marginTop: '24px' }}>
        <table className="admin-table customers-table" style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          minWidth: '1200px'
        }}>
          <thead style={{ background: '#ffffff', borderBottom: '2px solid #e5e7eb' }}>
            <tr>
              <th style={{ width: '50px', padding: '16px 8px', textAlign: 'left' }}>ID</th>
              <th style={{ width: '150px', padding: '16px 8px', textAlign: 'left' }}>NAME</th>
              <th style={{ width: '150px', padding: '16px 8px', textAlign: 'left' }}>EMAIL</th>
              <th style={{ width: '120px', padding: '16px 8px', textAlign: 'left' }}>PHONE</th>
              <th style={{ width: '100px', padding: '16px 8px', textAlign: 'center' }}>STATUS</th>
              <th style={{ width: '100px', padding: '16px 8px', textAlign: 'center' }}>ACCOUNTS</th>
              <th style={{ width: '120px', padding: '16px 8px', textAlign: 'right' }}>BALANCE</th>
              <th style={{ width: '150px', padding: '16px 8px', textAlign: 'right' }}>PORTFOLIO TOTAL</th>
              <th style={{ width: '100px', padding: '16px 8px', textAlign: 'center' }}>CRYPTO</th>
              <th style={{ width: '100px', padding: '16px 8px', textAlign: 'center' }}>ASSETS</th>
              <th style={{ width: '120px', padding: '16px 8px', textAlign: 'left' }}>CREATED</th>
              <th style={{ width: '150px', padding: '16px 8px', textAlign: 'center' }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {groupedCustomers.map((group) => (
              <React.Fragment key={group.customerName}>
                {/* Group Header */}
                <tr 
                  className={`customer-group-header ${expandedGroups.has(group.customerName) ? 'expanded' : ''}`}
                  onClick={() => toggleGroup(group.customerName)}
                >
                  <td colSpan={12}>
                    <div className="group-header-content">
                      <div className="group-header-left">
                        {expandedGroups.has(group.customerName) ? '🔽' : '▶️'} {group.customerName} ({group.customers.length} customer{group.customers.length !== 1 ? 's' : ''}{group.childCustomers.length > 0 ? ` + ${group.childCustomers.length} child customer${group.childCustomers.length !== 1 ? 's' : ''}` : ''})
                      </div>
                      <div className="group-header-right">
                        Total Balance: ${group.totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | 
                        Portfolio: ${group.totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Customer Details (shown when expanded) */}
                {expandedGroups.has(group.customerName) && (
                  <>
                    {/* Parent Customers */}
                    {group.customers.map((customer) => {
              // Calculate total balance across all accounts
              const totalBalance = customer.accounts?.reduce((sum, account) => 
                sum + parseFloat(account.balance || '0'), 0) || 0;
                      
                      // --- NEW LOGIC FOR ACCOUNT BUTTONS ---
                      // List of allowed currencies
                      const allowedCurrencies = ['USD', 'GBP', 'EUR'];
                      // Get the set of currencies the user already has
                      const userCurrencies = new Set((customer.accounts || []).map(acc => acc.currency));
                      // Count of user's own accounts
                      const accountCount = (customer.accounts || []).length;
                      // Only show add button if user has less than 3 accounts
                      const canAddAccount = accountCount < 3;
                      // For each allowed currency, show button only if user doesn't have it and can add
                      const addAccountButtons = allowedCurrencies
                        .filter(currency => !userCurrencies.has(currency) && canAddAccount)
                        .map(currency => (
                          <button
                            key={currency}
                            className="add-account-btn"
                            onClick={() => handleAddCurrencyAccount(customer.id, currency)}
                            title={`Add ${currency} Account`}
                          >
                            + {currency}
                          </button>
                        ));
                      // --- END NEW LOGIC ---
              
              return (
                        <tr key={customer.id} className="customer-row parent-customer">
                  <td>{customer.id}</td>
                  <td>
                    <div className="customer-name">
                      {customer.firstName} {customer.lastName}
                    </div>
                  </td>
                  <td>{customer.email}</td>
                  <td>{customer.phone}</td>
                  <td>
                    <span className={`status-badge ${customer.status.toLowerCase()}`}>
                      {customer.status}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div className="accounts-summary">
                      <span className="account-count">{customer.accounts?.length || 0}</span>
                      {customer.accounts && customer.accounts.length > 0 && (
                        <div className="account-types">
                          {customer.accounts.map((account, idx) => (
                            <span key={account.id} className="account-type-badge" title={`${account.accountType} - ${account.accountNumber}`}>
                              {account.currency}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="balance-amount">
                      ${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    {customer.accounts && customer.accounts.length > 0 && (
                      <div className="account-balances">
                        {customer.accounts.map((account) => (
                          <div key={account.id} className="account-balance-item">
                            {account.currency}: {parseFloat(account.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    ${(customer.portfolioSummary?.totalPortfolioValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div className="crypto-summary">
                      <div className="crypto-value">
                        ${(customer.portfolioSummary?.cryptoTotalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div className="crypto-count">
                        {customer.portfolioSummary?.cryptoCoinsCount || 0} coins
                      </div>
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div className="assets-summary">
                      <div className="assets-value">
                        ${(customer.portfolioSummary?.assetsTotalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div className="assets-count">
                        {customer.portfolioSummary?.assetsHoldingsCount || 0} holdings
                      </div>
                    </div>
                  </td>
                  <td>{customer.createdAt ? formatDate(customer.createdAt) : 'Invalid Date'}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="view-btn" 
                        onClick={() => setDetailsCustomerId(customer.id)}
                        title="View Details"
                      >
                        View
                      </button>
                      {customer.status === 'ACTIVE' ? (
                        <button 
                          className="suspend-btn"
                          onClick={() => handleSuspendCustomer(customer.id)}
                          title="Suspend Customer"
                        >
                          Suspend
                        </button>
                      ) : (
                        <button 
                          className="reactivate-btn"
                          onClick={() => handleReactivateCustomer(customer.id)}
                          title="Reactivate Customer"
                        >
                          Reactivate
                        </button>
                      )}
                              {/* Render add account buttons for allowed currencies */}
                              {addAccountButtons}
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {/* Child Customers Section */}
                    {group.childCustomers.length > 0 && (
                      <>
                        <tr 
                          className={`child-customers-header ${expandedChildSections.has(group.customerName) ? 'expanded' : ''}`}
                          onClick={(e) => toggleChildSection(group.customerName, e)}
                        >
                          <td colSpan={12}>
                            <div className="child-customers-label">
                              {expandedChildSections.has(group.customerName) ? '🔽' : '▶️'} Child Customers ({group.childCustomers.length})
                            </div>
                          </td>
                        </tr>
                        {expandedChildSections.has(group.customerName) && group.childCustomers.map((customer) => {
                          // Calculate total balance across all accounts
                          const totalBalance = customer.accounts?.reduce((sum, account) => 
                            sum + parseFloat(account.balance || '0'), 0) || 0;
                          
                          // --- NEW LOGIC FOR ACCOUNT BUTTONS ---
                          // List of allowed currencies
                          const allowedCurrencies = ['USD', 'GBP', 'EUR'];
                          // Get the set of currencies the user already has
                          const userCurrencies = new Set((customer.accounts || []).map(acc => acc.currency));
                          // Count of user's own accounts
                          const accountCount = (customer.accounts || []).length;
                          // Only show add button if user has less than 3 accounts
                          const canAddAccount = accountCount < 3;
                          // For each allowed currency, show button only if user doesn't have it and can add
                          const addAccountButtons = allowedCurrencies
                            .filter(currency => !userCurrencies.has(currency) && canAddAccount)
                            .map(currency => (
                      <button 
                                key={currency}
                        className="add-account-btn"
                                onClick={() => handleAddCurrencyAccount(customer.id, currency)}
                                title={`Add ${currency} Account`}
                              >
                                + {currency}
                              </button>
                            ));
                          // --- END NEW LOGIC ---

                          return (
                            <tr key={customer.id} className="customer-row child-customer">
                              <td className="double-indented">{customer.id}</td>
                              <td>
                                <div className="customer-name">
                                  {customer.firstName} {customer.lastName}
                                </div>
                              </td>
                              <td>{customer.email}</td>
                              <td>{customer.phone}</td>
                              <td>
                                <span className={`status-badge ${customer.status.toLowerCase()}`}>
                                  {customer.status}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div className="accounts-summary">
                                  <span className="account-count">{customer.accounts?.length || 0}</span>
                                  {customer.accounts && customer.accounts.length > 0 && (
                                    <div className="account-types">
                                      {customer.accounts.map((account, idx) => (
                                        <span key={account.id} className="account-type-badge" title={`${account.accountType} - ${account.accountNumber}`}>
                                          {account.currency}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <div className="balance-amount">
                                  ${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                {customer.accounts && customer.accounts.length > 0 && (
                                  <div className="account-balances">
                                    {customer.accounts.map((account) => (
                                      <div key={account.id} className="account-balance-item">
                                        {account.currency}: {parseFloat(account.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                ${(customer.portfolioSummary?.totalPortfolioValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div className="crypto-summary">
                                  <div className="crypto-value">
                                    ${(customer.portfolioSummary?.cryptoTotalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </div>
                                  <div className="crypto-count">
                                    {customer.portfolioSummary?.cryptoCoinsCount || 0} coins
                                  </div>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div className="assets-summary">
                                  <div className="assets-value">
                                    ${(customer.portfolioSummary?.assetsTotalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </div>
                                  <div className="assets-count">
                                    {customer.portfolioSummary?.assetsHoldingsCount || 0} holdings
                                  </div>
                                </div>
                              </td>
                              <td>{customer.createdAt ? formatDate(customer.createdAt) : 'Invalid Date'}</td>
                              <td>
                                <div className="action-buttons">
                                  <button 
                                    className="view-btn" 
                                    onClick={() => setDetailsCustomerId(customer.id)}
                                    title="View Details"
                                  >
                                    View
                                  </button>
                                  {customer.status === 'ACTIVE' ? (
                                    <button 
                                      className="suspend-btn"
                                      onClick={() => handleSuspendCustomer(customer.id)}
                                      title="Suspend Customer"
                                    >
                                      Suspend
                                    </button>
                                  ) : (
                                    <button 
                                      className="reactivate-btn"
                                      onClick={() => handleReactivateCustomer(customer.id)}
                                      title="Reactivate Customer"
                                    >
                                      Reactivate
                      </button>
                                  )}
                                  {/* Render add account buttons for allowed currencies */}
                                  {addAccountButtons}
                    </div>
                  </td>
                </tr>
              );
            })}
                      </>
                    )}
                  </>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 0}
          className="pagination-btn"
        >
          ← Previous
        </button>
        <span className="pagination-info">
          Page {currentPage + 1} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
          className="pagination-btn"
        >
          Next →
        </button>
      </div>
      {/* Customer Details Modal */}
      {detailsCustomerId !== null && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => { setDetailsCustomerId(null); setEditMode(false); }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, minWidth: 400, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 16 }}>Customer Details</h3>
            {detailsLoading ? (
              <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>
            ) : detailsError ? (
              <div style={{ color: 'red', padding: 32 }}>{detailsError}</div>
            ) : detailsCustomer ? (
              <>
                <div><b>Name:</b> {detailsCustomer.firstName} {detailsCustomer.lastName}</div>
                <div><b>Email:</b> {detailsCustomer.email}</div>
                <div><b>Phone:</b> {detailsCustomer.phone}</div>
                <div><b>Status:</b> {detailsCustomer.status}</div>
                <div><b>KYC Status:</b> {detailsCustomer.kycStatus || '-'}</div>
                <div><b>Risk Rating:</b> {detailsCustomer.riskRating || '-'}</div>
                <div style={{ margin: '16px 0 8px', fontWeight: 600 }}>SEPA & Risk Controls</div>
                {editMode ? (
                  <>
                    <div>
                      <label>User Type:</label>
                      <select value={editForm.userType} onChange={e => setEditForm({ ...editForm, userType: e.target.value })} style={{ width: '100%', padding: 6, marginBottom: 6 }}>
                        <option value="">-- Select --</option>
                        <option value="RETAIL">Retail</option>
                        <option value="BUSINESS_NORMAL">Business (Normal Risk)</option>
                        <option value="BUSINESS_HIGH_RISK">Business (High Risk)</option>
                      </select>
                    </div>
                    <div>
                      <label>Risk Category:</label>
                      <select value={editForm.riskCategory} onChange={e => setEditForm({ ...editForm, riskCategory: e.target.value })} style={{ width: '100%', padding: 6, marginBottom: 6 }}>
                        <option value="">-- Select --</option>
                        <option value="NORMAL">Normal</option>
                        <option value="HIGH_RISK">High Risk</option>
                      </select>
                    </div>
                    <div>
                      <label>Review Required:</label>
                      <input type="checkbox" checked={!!editForm.reviewRequired} onChange={e => setEditForm({ ...editForm, reviewRequired: e.target.checked })} style={{ marginLeft: 8 }} />
                    </div>
                    <div>
                      <label>SEPA Daily Limit:</label>
                      <input type="number" value={editForm.sepaDailyLimit} onChange={e => setEditForm({ ...editForm, sepaDailyLimit: e.target.value })} style={{ width: '100%', padding: 6, marginBottom: 6 }} min={0} />
                    </div>
                    <div>
                      <label>SEPA Monthly Limit:</label>
                      <input type="number" value={editForm.sepaMonthlyLimit} onChange={e => setEditForm({ ...editForm, sepaMonthlyLimit: e.target.value })} style={{ width: '100%', padding: 6, marginBottom: 6 }} min={0} />
                    </div>
                    <div>
                      <label>SEPA Yearly Limit:</label>
                      <input type="number" value={editForm.sepaYearlyLimit} onChange={e => setEditForm({ ...editForm, sepaYearlyLimit: e.target.value })} style={{ width: '100%', padding: 6, marginBottom: 6 }} min={0} />
                    </div>
                    <div>
                      <label>SEPA Single Transaction Limit:</label>
                      <input type="number" value={editForm.sepaSingleTransactionLimit} onChange={e => setEditForm({ ...editForm, sepaSingleTransactionLimit: e.target.value })} style={{ width: '100%', padding: 6, marginBottom: 6 }} min={0} />
                    </div>
                    <div>
                      <label>SEPA Limit Currency:</label>
                      <input type="text" value={editForm.sepaLimitCurrency} onChange={e => setEditForm({ ...editForm, sepaLimitCurrency: e.target.value })} style={{ width: '100%', padding: 6, marginBottom: 6 }} maxLength={3} />
                    </div>
                  </>
                ) : (
                  <>
                    <div><b>User Type:</b> {detailsCustomer.userType || '-'}</div>
                    <div><b>Risk Category:</b> {detailsCustomer.riskCategory || '-'}</div>
                    <div><b>Review Required:</b> {detailsCustomer.reviewRequired ? 'Yes' : 'No'}</div>
                    <div><b>SEPA Daily Limit:</b> {detailsCustomer.sepaDailyLimit ?? '-'} {detailsCustomer.sepaLimitCurrency || ''}</div>
                    <div><b>SEPA Monthly Limit:</b> {detailsCustomer.sepaMonthlyLimit ?? '-'} {detailsCustomer.sepaLimitCurrency || ''}</div>
                    <div><b>SEPA Yearly Limit:</b> {detailsCustomer.sepaYearlyLimit ?? '-'} {detailsCustomer.sepaLimitCurrency || ''}</div>
                    <div><b>SEPA Single Transaction Limit:</b> {detailsCustomer.sepaSingleTransactionLimit ?? '-'} {detailsCustomer.sepaLimitCurrency || ''}</div>
                  </>
                )}
                <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
                  <button style={{ padding: '8px 24px', borderRadius: 4, background: '#3b82f6', color: 'white', border: 'none', cursor: 'pointer' }} onClick={() => { setDetailsCustomerId(null); setEditMode(false); }}>Close</button>
                  {canEdit && !editMode && (
                    <button style={{ padding: '8px 24px', borderRadius: 4, background: '#10b981', color: 'white', border: 'none', cursor: 'pointer' }} onClick={() => setEditMode(true)}>Edit</button>
                  )}
                  {canEdit && editMode && (
                    <>
                      <button style={{ padding: '8px 24px', borderRadius: 4, background: '#10b981', color: 'white', border: 'none', cursor: 'pointer' }} onClick={handleSave}>Save</button>
                      <button style={{ padding: '8px 24px', borderRadius: 4, background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer' }} onClick={() => setEditMode(false)}>Cancel</button>
                    </>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement; 